// Einstellungen der E-Rechnung (SPEZIFIKATION §5.5): Standard-Syntax, Stichtag der Pflicht, Kleinbetragsgrenze –
// und die Prüfung „Bereit für E-Rechnungen?“ über die Angaben zum Betrieb (P-01 … P-04, Hinweis P-01b).
import { defineContract } from '@shared/contract'
import { z } from 'zod'
import { zDay, zFinding } from './common'

export const zEInvoiceSettings = z.object({
  /** Funktionsschalter `einvoice` (Einstellungen › Funktionen) */
  enabled: z.boolean(),
  defaultSyntax: z.enum(['ubl', 'cii']),
  /** Stichtag der Ausstellungspflicht; null = keine Pflicht hinterlegt */
  mandatoryFrom: z.string().nullable(),
  smallAmountCents: z.int(),
})
export type EInvoiceSettings = z.output<typeof zEInvoiceSettings>

export const settingsGet = defineContract({
  name: 'einvoice.settings.get',
  title: 'Einstellungen der E-Rechnung anzeigen',
  description:
    'Standard-Syntax, Stichtag der E-Rechnungspflicht, Kleinbetragsgrenze und ob die Angaben zum Betrieb für E-Rechnungen reichen.',
  llm: {
    description:
      'Liefert die Einstellungen der E-Rechnung (Syntax UBL/CII, ab wann die Ausstellungspflicht gilt, Kleinbetragsgrenze in Cent) und die Prüfung „Bereit für E-Rechnungen?“ – was in den Angaben zum Betrieb noch fehlt.',
    keywords: ['E-Rechnung', 'XRechnung', 'Stichtag', 'Pflicht', 'bereit'],
  },
  input: z.strictObject({}),
  output: zEInvoiceSettings.extend({
    editable: z.boolean(),
    /** Befunde zu den Angaben zum Betrieb (P-01 … P-04, P-01b) – leer = bereit */
    readiness: z.array(zFinding),
    ready: z.boolean(),
  }),
  permission: 'platform.settings.read',
  risk: 'read',
})

export const settingsUpdate = defineContract({
  name: 'einvoice.settings.update',
  title: 'Einstellungen der E-Rechnung ändern',
  description:
    'Setzt Standard-Syntax, Stichtag der E-Rechnungspflicht und Kleinbetragsgrenze. Nur die genannten Werte ändern sich.',
  llm: { description: 'Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    defaultSyntax: z.enum(['ubl', 'cii'], { error: 'Bitte wählen Sie UBL oder CII.' }).optional(),
    mandatoryFrom: zDay.nullable().optional(),
    smallAmountCents: z
      .int({ error: 'Bitte einen Betrag in Cent eingeben.' })
      .min(0, { error: 'Der Betrag darf nicht negativ sein.' })
      .max(100_000_000, { error: 'Der Betrag ist zu hoch.' })
      .optional(),
  }),
  output: zEInvoiceSettings,
  permission: 'einvoice.settings',
  risk: 'write',
  assistant: 'never',
})
